import React, { useCallback, useMemo, useState } from 'react';
import type { CrudTableColumn } from '../crud/CrudTable';
import CrudTable, { useCrudState, useCrudDeleteHandler } from '../crud/CrudTable';
import type { UpgradePathDiscountListFragment, UpgradePathDiscountSingleFragment } from '../../graphql/graphql';
import { useUpgradeService } from '../../services/UpgradeService';
import { ActionIcon, Badge, Flex, Group, Menu, Modal, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconTrash } from '@tabler/icons-react';
import StatusBadge from '../content/StatusBadge';
import { AddUpgradePathModal, EditUpgradePathModal } from './UpgradePathModal';

// Type guard to ensure path has full relations (fromProductSubscriptionType and targetProducts)
type UpgradePathWithFullRelations = UpgradePathDiscountListFragment & {
	fromProductSubscriptionType: NonNullable<UpgradePathDiscountListFragment['fromProductSubscriptionType']>;
	targetProducts: NonNullable<UpgradePathDiscountListFragment['targetProducts']>;
};

function hasFullRelations(path: UpgradePathDiscountListFragment): path is UpgradePathWithFullRelations {
	return (
		path.fromProductSubscriptionType !== null &&
		path.fromProductSubscriptionType !== undefined &&
		Array.isArray(path.targetProducts)
	);
}

type StatusFilter = 'all' | 'active' | 'inactive';

export interface UpgradePathsListProps {
	paths: UpgradePathDiscountListFragment[];
	isLoadingPaths: boolean;
	onPathsChanged: () => void;
	onPathCreated?: (created: UpgradePathDiscountSingleFragment) => void;
	onPathUpdated?: (updated: UpgradePathDiscountSingleFragment) => void;
}

// This component displays all upgrade paths in a table format with filtering capabilities.
// It's the main dashboard view for marketing teams to see what upgrade offers are currently active,
// what discounts are configured, and which products are involved in each upgrade path.
// The table shows source products, target products, discount amounts, time windows, and prompt counts.
export default function UpgradePathsList({
	paths,
	isLoadingPaths,
	onPathsChanged,
	onPathCreated,
	onPathUpdated,
}: UpgradePathsListProps) {
	const upgradeService = useUpgradeService();
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const crudState = useCrudState<UpgradePathDiscountListFragment, UpgradePathDiscountListFragment>();
	const [editingPath, setEditingPath] = useState<UpgradePathDiscountListFragment | null>(null);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);

	const handleCreate = useCallback(
		(created: UpgradePathDiscountSingleFragment) => {
			setIsAddModalOpen(false);
			onPathCreated?.(created);
			onPathsChanged();
		},
		[onPathCreated, onPathsChanged],
	);

	const handleUpdate = useCallback(
		(updated: UpgradePathDiscountSingleFragment) => {
			setEditingPath(null);
			onPathUpdated?.(updated);
			onPathsChanged();
		},
		[onPathUpdated, onPathsChanged],
	);

	const handleDelete = useCrudDeleteHandler({
		crudState,
		entityName: 'upgrade path',
		delete: async (path: UpgradePathDiscountListFragment) => {
			await upgradeService.deletePath(path.id);
			onPathsChanged();
		},
		nameAccessor: (path) => path.name,
	});

	const filteredItems = useMemo(() => {
		if (statusFilter === 'all') {
			return paths;
		}
		return paths.filter((item) => (statusFilter === 'active' ? item.isActive : !item.isActive));
	}, [paths, statusFilter]);

	return (
		<Stack gap="lg">
			<Flex justify="space-between" align="center">
				<Text fw={600}>Filter by status</Text>
				<SegmentedControl
					value={statusFilter}
					onChange={(value) => setStatusFilter(value as StatusFilter)}
					data={[
						{ label: 'All', value: 'all' },
						{ label: 'Active', value: 'active' },
						{ label: 'Inactive', value: 'inactive' },
					]}
				/>
			</Flex>
			<CrudTable
				entityName="upgrade path"
				headers={crudHeaders}
				columns={(path) =>
					buildColumns(path, {
						onEdit: setEditingPath,
						onDelete: handleDelete,
					})
				}
				items={filteredItems}
				isLoading={isLoadingPaths}
				onAddClicked={() => setIsAddModalOpen(true)}
			/>

			<Modal opened={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add upgrade path" size="xl">
				<AddUpgradePathModal
					modalId="add-upgrade-path"
					onCancel={() => setIsAddModalOpen(false)}
					onCreated={handleCreate}
				/>
			</Modal>

			<Modal
				opened={Boolean(editingPath)}
				onClose={() => setEditingPath(null)}
				title="Edit upgrade path"
				size="xl"
			>
				{editingPath && (
					<EditUpgradePathModal
						modalId={`edit-${editingPath.id}`}
						onCancel={() => setEditingPath(null)}
						current={editingPath}
						onEdited={handleUpdate}
					/>
				)}
			</Modal>
		</Stack>
	);
}

const crudHeaders: CrudTableColumn[] = [
	{ children: 'Name' },
	{ children: 'From Subscription' },
	{ children: 'To Subscription' },
	{ children: 'Days Min-Max' },
	{ children: 'Discount %' },
	{ children: 'Credit Mode' },
	{ children: 'Active' },
	{ children: 'Prompts' },
	{ children: '' },
];

/**
 * Determines the effective credit mode for display purposes.
 * Priority: creditAllPriorPayments > applyCreditForExistingPurchase > none
 * (matches backend logic in UpgradeDiscountCalculator)
 */
function getCreditModeDisplay(path: UpgradePathDiscountListFragment): {
	label: string;
	color: 'green' | 'dimmed';
} {
	if (path.creditAllPriorPayments) {
		return { label: 'All Payments', color: 'green' };
	}
	if (path.applyCreditForExistingPurchase) {
		return { label: 'Most Recent', color: 'green' };
	}
	return { label: 'None', color: 'dimmed' };
}

interface CrudHandlers {
	onEdit: (path: UpgradePathDiscountListFragment) => void;
	onDelete: (path: UpgradePathDiscountListFragment) => void;
}

function buildColumns(path: UpgradePathDiscountListFragment, crudHandlers: CrudHandlers): CrudTableColumn[] {
	return [
		{
			children: (
				<Stack gap={2}>
					<Text fw={600}>{path.name}</Text>
					{path.description && (
						<Text size="sm" c="dimmed" lineClamp={2}>
							{path.description}
						</Text>
					)}
				</Stack>
			),
		},
		{
			children: path.fromProductSubscriptionType ? (
				<Stack gap={2}>
					<Text size="sm" fw={500}>
						{path.fromProductSubscriptionType.name}
					</Text>
					{path.fromProductSubscriptionType.product && (
						<Text size="xs" c="dimmed">
							{path.fromProductSubscriptionType.product.shortName ??
								path.fromProductSubscriptionType.product.name}
						</Text>
					)}
				</Stack>
			) : (
				<Text size="sm" c="dimmed">
					Any subscription
				</Text>
			),
		},
		{
			children:
				// Show all target subscription types with their CTA text in tooltips
				hasFullRelations(path) && path.targetProducts.length > 0 ? (
					<Group gap={6} wrap="wrap">
						{path.targetProducts.map((tp) => (
							<Tooltip
								key={tp.id}
								label={
									<Stack gap={4}>
										<Text size="xs" fw={500}>
											{tp.productSubscriptionType?.name ?? 'Subscription Type'}
										</Text>
										{tp.productSubscriptionType?.product && (
											<Text size="xs" c="dimmed">
												{tp.productSubscriptionType.product.shortName ??
													tp.productSubscriptionType.product.name}
											</Text>
										)}
									</Stack>
								}
							>
								<Badge variant="light" color="grape">
									{tp.productSubscriptionType?.name ?? 'Unknown'}
								</Badge>
							</Tooltip>
						))}
					</Group>
				) : (
					<Text size="sm" c="dimmed">
						No targets configured
					</Text>
				),
		},
		{
			children: (
				<Text size="sm">
					{path.daysAfterPurchaseMin ?? 0} - {path.daysAfterPurchaseMax ?? '∞'}
				</Text>
			),
		},
		{
			children: (
				<Text size="sm">{path.discountPercentage ? `${Math.round(path.discountPercentage * 100)}%` : '—'}</Text>
			),
		},
		{
			children: (() => {
				const creditMode = getCreditModeDisplay(path);
				return (
					<Text size="sm" c={creditMode.color}>
						{creditMode.label}
					</Text>
				);
			})(),
		},
		{
			children: <StatusBadge status={path.isActive ? 'published' : 'draft'} />,
		},
		{
			children: (
				<Text size="sm" ta="center">
					{path._count?.upgradePrompts ?? 0}
				</Text>
			),
		},
		{
			children: (
				<Flex justify="flex-end">
					<Menu shadow="md" width={160}>
						<Menu.Target>
							<ActionIcon variant="subtle" color="dark">
								<IconDotsVertical size={18} />
							</ActionIcon>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item leftSection={<IconPencil size={16} />} onClick={() => crudHandlers.onEdit(path)}>
								Edit
							</Menu.Item>
							<Menu.Item
								leftSection={<IconTrash size={16} />}
								color="red"
								onClick={() => crudHandlers.onDelete(path)}
							>
								Delete
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</Flex>
			),
		},
	];
}
